import React, { useCallback, useState } from "react";
import { get } from "lodash";
import styled from "styled-components";
import { FontWeight, content, ellipsis, transparentize } from "@glossy";
import { Radius } from "@glossy/radii";
import Image from "@ui-kit/utils/image";
import { getCoverFallback } from "@radarly/models/picture";
import { Post, PostLensFactory } from "@radarly/models/posts";
import { Project } from "@radarly/models/project";

interface IProps {
  width: number;
  height?: number;
  post: Post;
  project?: Project;
  largePreview?: boolean;
  onThumbnailClick?: (image: string) => void;
}

const Root = styled.div`
  cursor: pointer;
`;

const LargePreview = styled.a`
  padding: 12px;
  border: 1px solid;
  border-color: ${(props) => content(props, "tertiary")};
  border-radius: ${Radius.Small};
  overflow: hidden;
  display: flex;
  :hover {
    background: ${(props) => transparentize(0.9, content(props, "secondary"))};
  }
  :focus {
    text-decoration: none;
  }
`;

const LargePreviewContent = styled.div`
  margin-left: 12px;
  overflow: hidden;
`;

const Platform = styled.div`
  text-transform: uppercase;
  color: ${(props) => content(props, "secondary")};
`;
const Title = styled.div`
  font-weight: ${FontWeight.Bold};
  color: ${content};
`;
const Description = styled.div`
  color: ${(props) => content(props, "secondary")};
  ${ellipsis()};
`;

const MIN_WIDTH_ATTACHMENT_LEFT = 320;

const ImageWrapper = styled.div<{
  width: number;
  height: number;
  largePreview?: boolean;
}>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  overflow: hidden;
  width: ${(props) => props.width}px;
  height: ${(props) => props.height}px;
  background: ${(props) => transparentize(0.5, content(props, "tertiary"))};
  color: ${(props) => content(props, "secondary")};
`;

const RadarlyPostAttachments = ({
  width,
  height,
  post,
  project,
  largePreview = false,
  onThumbnailClick = () => null,
}: IProps) => {
  const [shownImage, setShownImage] = useState(null);
  const lens = PostLensFactory.create(post);
  const largePreviewData = lens.getLargePreviewData();
  const attachment =
    lens.getCover() || (!largePreview && get(largePreviewData, "thumbnail"));
  const fallbackImage = getCoverFallback(lens, project);
  const openPreview = useCallback(
    (event: React.MouseEvent) => {
      if (event.target instanceof Element && largePreviewData.link) {
        event.stopPropagation();
        window.open(largePreviewData.link);
      }
    },
    [largePreviewData]
  );

  return (
    <Root onClick={() => onThumbnailClick(shownImage)}>
      {largePreview ? (
        largePreviewData && (
          <LargePreview onClick={openPreview}>
            <ImageWrapper width={width} height={height} largePreview>
              <Image
                image={largePreviewData.thumbnail || attachment}
                fallbackImage={fallbackImage}
                onLoad={(image) => {
                  setShownImage(image);
                }}
                lazy
                animated
              />
            </ImageWrapper>
            <LargePreviewContent>
              <Platform>{largePreviewData.caption}</Platform>
              <Title>{largePreviewData.name}</Title>
              <Description>{largePreviewData.description}</Description>
            </LargePreviewContent>
          </LargePreview>
        )
      ) : (
        <>
          {width <= MIN_WIDTH_ATTACHMENT_LEFT && attachment && (
            <ImageWrapper width={width} height={height}>
              <Image
                image={attachment}
                fallbackImage={fallbackImage}
                onLoad={(image) => {
                  setShownImage(image);
                }}
                lazy
                animated
              />
            </ImageWrapper>
          )}
        </>
      )}
    </Root>
  );
};

export default RadarlyPostAttachments;
